import type { EnergyMember } from '@slims/shared/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';

const EMPTY: EnergyMember[] = [];
export const energyMembersKey = (orgId: string | null) => ['energy-members', orgId] as const;
export const fetchEnergyMembers = (orgId: string) =>
	api.get<EnergyMember[]>('/api/members/energy-summary', { orgId });

/** Read-only member data for configuration dashboards, separate from admin records. */
export function useEnergyMembers(orgId: string | null) {
	const queryClient = useQueryClient();
	const { leaves } = useScopeTree();
	const tenantId = leaves.find((leaf) => leaf.orgId === orgId)?.tenantId;
	const { data, isLoading } = useQuery({
		queryKey: energyMembersKey(orgId),
		enabled: !!orgId,
		queryFn: () => fetchEnergyMembers(orgId as string),
	});
	useEffect(() => {
		if (!tenantId) return;
		return subscribeToTable(
			`energy-members-${tenantId}`,
			{ event: '*', schema: 'public', table: 'members', filter: `tenant_id=eq.${tenantId}` },
			() => {
				void queryClient.invalidateQueries({ queryKey: energyMembersKey(orgId) });
			},
		);
	}, [orgId, tenantId, queryClient]);
	return { members: data ?? EMPTY, isLoading };
}
